import { AuthContext, isTabletPortraitDown } from '@enter/core';
import { PageLayoutHeader, PageLayoutRow, PageLayoutTitle } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { RootStore } from 'stores';
import { Banking, Members, Overview, SecurityAndSafety } from './index';
import * as S from './settings.styles';
import { isActiveFor } from './shared';
import {Api} from "./api";
import {Webhooks} from "./webhooks";
import {SingleSignOn} from "./sso";
import {Hl7Settings} from "./hl7";
import {SftpSettings} from "./sftp";
import {TerminalSettings} from "./terminal";
import {Invoices} from "./invoices";

export const Settings = inject('rootStore')(
    observer((props: {}) => {
        const rootStore: RootStore = _.get(props, 'rootStore');
        if (!rootStore) {
            return <></>;
        }

        const { settingsStore } = rootStore;

        const authContext = useContext(AuthContext);

        useEffect(() => {
            document.title = 'Provider Dashboard - Settings';
            authContext.setGroup('settings');
            settingsStore.initSettings();

            return () => {
                authContext.cancelGroup('settings');
            };
        }, []);

        const renderMobile = (): React.ReactElement => {
            return (
                <Switch>
                    <Route path="/settings" exact={true} render={this.renderCategoryColumn} />
                    <Route path="/settings/overview" component={Overview} />
                    <Route path="/settings/security-and-safety" component={SecurityAndSafety} />
                    <Route path="/settings/banking" exact={true} component={Banking} />
                    <Route path="/settings/members" exact={true} component={Members} />
                    <Route path="/settings/api" component={Api}/>
                    <Route path="/settings/webhooks" component={Webhooks}/>
                    <Route path="/settings/invoices" component={Invoices} exact={true} />
                </Switch>
            );
        };

        // NB: visible only for desktop, or if mobile viewing '/settings'
        const renderCategoryColumn = (): React.ReactElement => {
            return (
                <S.CategoryColumn>
                    <S.CategoryHeader>Account</S.CategoryHeader>
                    <S.Link to="/settings/overview" isActive={isActiveFor('/settings')}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Overview</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/security-and-safety">
                        {(props): React.ReactElement => (
                            <S.CategoryItem active={props.active}>Security & Safety</S.CategoryItem>
                        )}
                    </S.Link>
                    <S.Link to="/settings/banking">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Banking</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/invoices">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Invoices</S.CategoryItem>}
                    </S.Link>
                    <S.CategoryHeader>User & Team Management</S.CategoryHeader>
                    <S.Link to="/settings/members">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Members</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/sso">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Single Sign-On</S.CategoryItem>}
                    </S.Link>
                    <S.CategoryHeader>Development</S.CategoryHeader>
                    <S.Link to="/settings/api">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>API Keys</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/hl7">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>HL7</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/sftp">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>SFTP</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/terminal">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Terminal</S.CategoryItem>}
                    </S.Link>
                    <S.Link to="/settings/webhooks">
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Webhooks</S.CategoryItem>}
                    </S.Link>
                </S.CategoryColumn>
            );
        };
        // NB: visible only for desktop, or if mobile viewing '/settings/[overview|security|banking|members]'
        const renderSubCategoryColumn = (): React.ReactElement => {
            return (
                <S.SubCategoryColumn>
                    <Switch>
                        <Route path="/settings/overview" component={Overview} />
                        <Route path="/settings/security-and-safety" component={SecurityAndSafety} />
                        <Route path="/settings/banking" component={Banking} />
                        <Route path="/settings/members" component={Members} />
                        <Route path="/settings/api" component={Api}/>
                        <Route path="/settings/webhooks" component={Webhooks}/>
                        <Route path="/settings/sso" component={SingleSignOn}/>
                        <Route path="/settings/hl7" component={Hl7Settings}/>
                        <Route path="/settings/sftp" component={SftpSettings}/>
                        <Route path="/settings/terminal" component={TerminalSettings}/>
                        <Route path="/settings/invoices" component={Invoices}/>
                        {/* NB: for simplified logic, just add a route for the default case */}
                        <Route path="/settings" component={Overview} exact={true} />
                    </Switch>
                </S.SubCategoryColumn>
            );
        };

        return (
            <S.SettingsContainer>
                <S.SettingsHeader>
                    <PageLayoutRow>
                        <PageLayoutHeader>
                            <PageLayoutTitle>
                                <S.Link to="/settings">{(props): React.ReactElement => <span>Settings</span>}</S.Link>
                            </PageLayoutTitle>
                        </PageLayoutHeader>
                    </PageLayoutRow>
                </S.SettingsHeader>
                <S.SettingsArea>
                    {isTabletPortraitDown() && renderMobile()}
                    {!isTabletPortraitDown() && renderCategoryColumn()}
                    {!isTabletPortraitDown() && renderSubCategoryColumn()}
                </S.SettingsArea>
            </S.SettingsContainer>
        );
    }),
);
